/**
 * Web Push subscription hook.
 *
 * Espone:
 *   - supported      : il browser supporta PushManager + Notification
 *   - configured     : il backend ha le chiavi VAPID
 *   - permission     : 'default' | 'granted' | 'denied'
 *   - subscribed     : bool — esiste già una sub registrata sul backend
 *   - loading        : in corso una operazione
 *   - error          : ultimo errore (string) o null
 *   - enable()       : chiede permesso → subscribe → POST al backend
 *   - disable()      : unsubscribe locale + DELETE backend
 *   - testPush()     : invia una push di test (per verificare end-to-end)
 *
 * Restrizione: in dev (`NODE_ENV !== 'production'`) il service worker non
 * è registrato (vedi index.js), quindi `supported` resta false. Questo è
 * intenzionale per evitare di rompere l'hot-reload.
 */
import { useEffect, useState, useCallback } from 'react';
import { pushApi } from './api';

function urlBase64ToUint8Array(base64String) {
  // VAPID public key arriva base64url (no padding). PushManager.subscribe
  // vuole un Uint8Array.
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i);
  return out;
}

export default function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Capability detection + initial state sync
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ok =
          typeof window !== 'undefined' &&
          'serviceWorker' in navigator &&
          'PushManager' in window &&
          'Notification' in window;
        if (cancelled) return;
        setSupported(ok);
        if (!ok) return;

        // VAPID configurato lato server?
        try {
          const r = await pushApi.publicKey();
          if (cancelled) return;
          setConfigured(!!r.data?.configured);
        } catch {
          setConfigured(false);
        }

        // Stato subscription corrente
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            if (!cancelled) setSubscribed(!!sub);
          }
        } catch { /* */ }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (!supported) throw new Error('Notifiche non supportate da questo browser');
      // Permission gating
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }
      setPermission(perm);
      if (perm !== 'granted') {
        throw new Error('Permesso notifiche negato dal browser');
      }

      const reg = await navigator.serviceWorker.ready;
      if (!reg) throw new Error('Service worker non registrato (è disponibile solo in produzione)');

      // Riusa sub esistente o creane una nuova
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const pk = await pushApi.publicKey();
        const publicKey = pk.data?.public_key;
        if (!publicKey) throw new Error('Web Push non configurato sul server');
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      // Estrai endpoint + keys (PushSubscription.toJSON())
      const subJson = sub.toJSON();
      await pushApi.subscribe({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });
      setSubscribed(true);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Errore attivazione notifiche';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      let endpoint = null;
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          endpoint = sub.endpoint;
          await sub.unsubscribe();
        }
      }
      // Backend: rimuovi specifica sub se l'abbiamo, altrimenti tutte
      await pushApi.unsubscribe({ endpoint });
      setSubscribed(false);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Errore disattivazione';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const testPush = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      await pushApi.test();
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Errore invio test';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    supported,
    configured,
    permission,
    subscribed,
    loading,
    error,
    enable,
    disable,
    testPush,
  };
}
